import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@/hooks/useNavigate';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useClientDetails } from '@/api/services';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { actionColor } from '@/lib/brandColor';
import { useBrandColors } from '@/hooks/useBrandColors';
import { useEffectiveLanguage } from '@/hooks/useEffectiveLanguage';
import { ApplyFlowShell } from '@/pages/ApplyJob/components/ApplyFlowShell';
import { StepProgress } from '@/pages/ApplyJob/components/StepProgress';
import LocationRow from './LocationRow';
import {
  toApplicableLocations,
  filterLocations,
  shouldShowSearch,
  applyFormPath,
} from './locationUtils';

const ClientLocations = () => {
  const [language, setLanguage] = useAppLanguage();
  const { t } = useTranslation('applyJob');
  const navigate = useNavigate();
  const params = useParams<{ client_id: string }>();
  const { data, isLoading, isError } = useClientDetails(
    params.client_id || '',
    language
  );

  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { backgroundColor, textColor } = useBrandColors(data);

  const clientPositions = useMemo(
    () => data?.properties.flatMap((property) => property.positions),
    [data]
  );
  const { availableLanguages, effectiveLanguage } = useEffectiveLanguage(
    language,
    clientPositions
  );

  const locations = useMemo(() => toApplicableLocations(data?.properties), [data]);
  const filtered = useMemo(() => filterLocations(locations, query), [locations, query]);
  const showSearch = shouldShowSearch(locations.length);

  usePageTitle(data?.client_name, 'apply');

  if (isLoading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5] text-[#9aa0a8]">
        <div className="size-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    );
  }

  const noData =
    isError || !data || locations.length === 0
      ? { title: t('no_locations_title'), body: t('no_locations_body') }
      : undefined;

  const onContinue = () => {
    if (selectedId == null) return;
    navigate(applyFormPath(selectedId));
  };

  return (
    <ApplyFlowShell
      bgColor={backgroundColor}
      inkColor={textColor}
      logoUrl={data?.logo_url ?? ''}
      brandName={data?.client_name}
      welcomeTitle={
        <Trans
          i18nKey="applyJob:welcome_title"
          components={{
            br: <br />,
          }}
        />
      }
      language={effectiveLanguage}
      availableLanguages={availableLanguages}
      onLanguageChange={setLanguage}
      noData={noData}
      footer={
        <Button
          variant="brand"
          className="w-full gap-2 rounded-xl max-w-md"
          size="lg"
          disabled={selectedId == null}
          onClick={onContinue}
        >
          {t('continue_button')}
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      }
    >
      {data && (
        <>
          <StepProgress
            step={1}
            brandColor={actionColor(backgroundColor)}
            label={t('step_progress', { step: 1, total: 3 })}
            lastStepLabel={t('interview_step')}
          />
          <div className="mt-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-[#8a8f98]">
              {t('choose_location_subtitle')}
            </div>
            {showSearch && (
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search_placeholder')}
                aria-label={t('search_placeholder')}
                className="h-12 w-full rounded-xl border border-[#e3e4e8] bg-white px-4 text-sm text-[#16171a] outline-none placeholder:text-[#9aa0a8]"
              />
            )}

            <div
              className={cn(
                'text-xs font-bold uppercase tracking-wide text-[#8a8f98]',
                showSearch && 'mt-4'
              )}
            >
              {t('locations_count', { count: locations.length })}
            </div>

            {filtered.length === 0 ? (
              <div className="mt-4 text-center text-sm font-semibold text-[#6b7079]">
                {t('no_search_results')}
              </div>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                {filtered.map((loc) => (
                  <LocationRow
                    key={loc.id}
                    location={loc}
                    selected={loc.id === selectedId}
                    onSelect={setSelectedId}
                    rolesLabel={t('roles_hiring', { count: loc.rolesCount })}
                    mapLabel={t('map_view')}
                    inProgressLabel={
                      loc.inProgressCount > 0
                        ? t('location_in_progress', {
                            count: loc.inProgressCount,
                          })
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </ApplyFlowShell>
  );
};

export default ClientLocations;
